import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";

import BackButton from "../../shared/components/BackButton";
import BrandSpinner from "../../shared/components/BrandSpinner";
import api from "../../services/api/axios";
import { BRAND_BLUE, BRAND_GREEN } from "../../theme/colors";
import { MEMBERSHIP_TOOLTIP_PROPS } from "./tooltipStyles";
import { PRIMARY_BUTTON_SX, SECONDARY_BUTTON_SX } from "../../theme/buttonstyles";
import MembershipMemberDialog from "./MembershipMemberDialog";

const PAGE_BG = "#f7f9fc";

const CARD_SX = {
  border: "1px solid rgba(0,51,108,0.10)",
  borderRadius: 2,
  boxShadow: "0 2px 12px rgba(0,51,108,0.08)",
  background: "#ffffff",
};

const STATUS_CONFIG = {
  active: { label: "Active", bg: "#E8F5E9", color: "#2E7D32", border: "#43A047" },
  prospect: { label: "Prospect", bg: "#E3F2FD", color: "#0D47A1", border: "#1976D2" },
  lapsed: { label: "Lapsed", bg: "#FFF8E1", color: "#E65100", border: "#FFB300" },
  suspended: { label: "Suspended", bg: "#FCE4EC", color: "#AD1457", border: "#F06292" },
  cancelled: { label: "Cancelled", bg: "#F5F5F5", color: "#616161", border: "#9E9E9E" },
};

const TABS = [
  "Overview",
  "Contacts",
  "Membership History",
  "Events",
  "Sponsorships",
  "Files",
  "Reports",
  "Timeline",
];

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function display(value) {
  return value == null || value === "" ? "-" : String(value);
}

function StatusChip({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.active;
  return (
    <Chip
      size="small"
      label={cfg.label}
      sx={{
        height: 24,
        fontSize: 12,
        fontWeight: 700,
        bgcolor: cfg.bg,
        color: cfg.color,
        border: `1px solid ${cfg.border}`,
      }}
    />
  );
}

function getCurrentMembership(memberships = []) {
  return [...memberships].sort((a, b) => {
    const aDate = a.renewal_date || "9999-12-31";
    const bDate = b.renewal_date || "9999-12-31";
    return aDate.localeCompare(bDate);
  })[0];
}

function getAtRiskIndicators(member, currentMembership) {
  const indicators = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (["lapsed", "suspended", "cancelled"].includes(member?.status)) {
    indicators.push(`Member status is ${member.status}.`);
  }

  if (currentMembership?.renewal_date) {
    const renewal = new Date(`${currentMembership.renewal_date}T00:00:00`);
    const diffDays = Math.ceil((renewal - today) / 86400000);
    if (diffDays < 0) indicators.push("Renewal date is overdue.");
    if (diffDays >= 0 && diffDays <= 30) indicators.push("Renewal is due within 30 days.");
  } else {
    indicators.push("No renewal date is on file.");
  }

  return indicators;
}

function SectionHeader({ children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
      <Box sx={{ width: 4, height: 22, borderRadius: 2, bgcolor: BRAND_GREEN }} />
      <Typography sx={{ color: BRAND_BLUE, fontWeight: 800 }}>{children}</Typography>
    </Box>
  );
}

function MetricCard({ label, value, helper }) {
  return (
    <Card sx={{ ...CARD_SX, height: "100%" }}>
      <CardContent sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <Typography sx={{ color: "#52677f", fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}>
          {label}
        </Typography>
        <Typography sx={{ color: BRAND_BLUE, fontSize: 30, fontWeight: 800, mt: 1 }}>
          {value}
        </Typography>
        {helper ? (
          <Typography sx={{ color: "#64748b", fontSize: 13, mt: 0.75 }}>{helper}</Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

function EngagementCategoryChip({ category }) {
  const normalized = String(category || "unscored").toLowerCase();
  const config = {
    high: { bg: "#E8F5E9", color: "#2E7D32", border: "#43A047" },
    moderate: { bg: "#E3F2FD", color: "#0D47A1", border: "#1976D2" },
    low: { bg: "#FFF8E1", color: "#E65100", border: "#FFB300" },
    unscored: { bg: "#F5F5F5", color: "#616161", border: "#9E9E9E" },
  }[normalized] || { bg: "#F5F5F5", color: "#616161", border: "#9E9E9E" };
  return (
    <Chip
      size="small"
      label={category || "unscored"}
      sx={{
        height: 24,
        fontSize: 12,
        fontWeight: 700,
        textTransform: "capitalize",
        bgcolor: config.bg,
        color: config.color,
        border: `1px solid ${config.border}`,
      }}
    />
  );
}

function DataTable({ columns, rows, emptyLabel, footerContent = null }) {
  if (!rows.length && !footerContent) {
    return (
      <Card sx={CARD_SX}>
        <CardContent sx={{ p: 4, textAlign: "center" }}>
          <Typography sx={{ color: BRAND_BLUE, fontWeight: 800 }}>No records found</Typography>
          <Typography sx={{ color: "#64748b", mt: 0.75 }}>{emptyLabel}</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <TableContainer component={Paper} elevation={0} sx={{ ...CARD_SX, overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 820 }}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.key}
                sx={{
                  bgcolor: BRAND_BLUE,
                  color: "#ffffff",
                  fontSize: 12,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  whiteSpace: "nowrap",
                }}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length ? (
            rows.map((row, index) => (
              <TableRow key={row.id || index} sx={{ bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff" }}>
                {columns.map((column) => (
                  <TableCell key={column.key} sx={{ color: BRAND_BLUE, fontWeight: 500 }}>
                    {column.render ? column.render(row) : display(row[column.key])}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} sx={{ py: 4, textAlign: "center" }}>
                <Typography sx={{ color: BRAND_BLUE, fontWeight: 800 }}>No records found</Typography>
                <Typography sx={{ color: "#64748b", mt: 0.75 }}>{emptyLabel}</Typography>
              </TableCell>
            </TableRow>
          )}
          {footerContent ? (
            <TableRow>
              <TableCell colSpan={columns.length} sx={{ py: 1.5, textAlign: "center", bgcolor: "#ffffff" }}>
                {footerContent}
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    color: BRAND_BLUE,
    background: "linear-gradient(180deg, rgba(231,240,218,0.25) 0%, #ffffff 80%)",
    boxShadow: "0 4px 14px rgba(0,51,108,0.10)",
    "& fieldset": { border: `2px solid ${BRAND_BLUE}` },
    "&:hover fieldset": { borderColor: BRAND_BLUE },
    "&.Mui-focused fieldset": { borderColor: BRAND_BLUE, borderWidth: 2 },
  },
  "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused": {
    color: BRAND_BLUE,
    fontWeight: 600,
  },
};

function FormDialog({ open, title, saving, onClose, onSubmit, children, submitLabel = "Save" }) {
  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ color: BRAND_BLUE, fontWeight: 800 }}>{title}</DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#fbfcf8" }}>
        <Box sx={{ pt: 1 }}>{children}</Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={SECONDARY_BUTTON_SX}>Cancel</Button>
        <Button onClick={onSubmit} disabled={saving} sx={PRIMARY_BUTTON_SX}>
          {saving ? "Saving..." : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const EMPTY_CONTACT_FORM = {
  contact_id: "",
  full_name: "",
  first_name: "",
  last_name: "",
  job_title: "",
  email: "",
  direct_phone_number: "",
  cell_phone_number: "",
  linkedin: "",
  notes: "",
  is_primary: false,
  is_billing: false,
};

function ContactDialog({ open, contact, saving, onClose, onSubmit }) {
  const [values, setValues] = useState(EMPTY_CONTACT_FORM);

  useEffect(() => {
    if (!open) return;
    setValues({
      ...EMPTY_CONTACT_FORM,
      ...(contact || {}),
      contact_id: contact?.id || "",
      is_primary: Boolean(contact?.link?.is_primary),
      is_billing: Boolean(contact?.link?.is_billing),
    });
  }, [contact, open]);

  const setValue = (field) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const submit = () => {
    const fullName = values.full_name || [values.first_name, values.last_name].filter(Boolean).join(" ");
    const contactPayload = {
      full_name: fullName,
      first_name: values.first_name,
      last_name: values.last_name,
      job_title: values.job_title,
      email: values.email,
      direct_phone_number: values.direct_phone_number,
      cell_phone_number: values.cell_phone_number,
      linkedin: values.linkedin,
      notes: values.notes,
    };
    const linkPayload = {
      is_primary: values.is_primary,
      is_billing: values.is_billing,
    };
    onSubmit({
      contactId: values.contact_id,
      linkId: contact?.link?.id,
      contactPayload,
      linkPayload,
      shouldUpdateContact: Boolean(contact?.id) || !values.contact_id,
    });
  };

  return (
    <FormDialog
      open={open}
      title={contact?.id ? "Edit Contact" : "Add Contact"}
      saving={saving}
      onClose={onClose}
      onSubmit={submit}
      submitLabel="Save Contact"
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
          gap: 2,
        }}
      >
        <Box>
          <TextField label="First Name" value={values.first_name} onChange={setValue("first_name")} fullWidth disabled={!contact?.id && Boolean(values.contact_id)} sx={FIELD_SX} />
        </Box>
        <Box>
          <TextField label="Last Name" value={values.last_name} onChange={setValue("last_name")} fullWidth disabled={!contact?.id && Boolean(values.contact_id)} sx={FIELD_SX} />
        </Box>
        <Box>
          <TextField label="Job Title" value={values.job_title} onChange={setValue("job_title")} fullWidth disabled={!contact?.id && Boolean(values.contact_id)} sx={FIELD_SX} />
        </Box>
        <Box>
          <TextField label="Email" value={values.email} onChange={setValue("email")} fullWidth disabled={!contact?.id && Boolean(values.contact_id)} sx={FIELD_SX} />
        </Box>
        <Box>
          <TextField label="Cell Phone" value={values.cell_phone_number} onChange={setValue("cell_phone_number")} fullWidth disabled={!contact?.id && Boolean(values.contact_id)} sx={FIELD_SX} />
        </Box>
        <Box>
          <TextField label="Direct Phone" value={values.direct_phone_number} onChange={setValue("direct_phone_number")} fullWidth disabled={!contact?.id && Boolean(values.contact_id)} sx={FIELD_SX} />
        </Box>
        <Box>
          <TextField label="LinkedIn" value={values.linkedin} onChange={setValue("linkedin")} fullWidth disabled={!contact?.id && Boolean(values.contact_id)} sx={FIELD_SX} />
        </Box>
        <Box
          sx={{
            minHeight: 56,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            px: 1,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={values.is_primary}
                onChange={setValue("is_primary")}
                icon={<RadioButtonUncheckedIcon />}
                checkedIcon={<RadioButtonCheckedIcon />}
                sx={{ color: BRAND_BLUE, "&.Mui-checked": { color: BRAND_GREEN } }}
              />
            }
            label="Primary contact"
            sx={{ m: 0, "& .MuiFormControlLabel-label": { color: BRAND_BLUE, fontWeight: 700 } }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={values.is_billing}
                onChange={setValue("is_billing")}
                icon={<RadioButtonUncheckedIcon />}
                checkedIcon={<RadioButtonCheckedIcon />}
                sx={{ color: BRAND_BLUE, "&.Mui-checked": { color: BRAND_GREEN } }}
              />
            }
            label="Billing contact"
            sx={{ m: 0, "& .MuiFormControlLabel-label": { color: BRAND_BLUE, fontWeight: 700 } }}
          />
        </Box>
        <Box sx={{ gridColumn: "1 / -1" }}>
          <TextField label="Notes" value={values.notes} onChange={setValue("notes")} fullWidth multiline minRows={3} disabled={!contact?.id && Boolean(values.contact_id)} sx={FIELD_SX} />
        </Box>
      </Box>
    </FormDialog>
  );
}

function useTabData(active, loader, reloadKey = 0) {
  const [state, setState] = useState({
    loading: false,
    error: "",
    data: null,
    loaded: false,
  });
  const loadedForKeyRef = useRef(null);

  useEffect(() => {
    if (!active || loadedForKeyRef.current === reloadKey) return undefined;

    let mounted = true;
    setState((prev) => ({ ...prev, loading: true, error: "", loaded: false }));
    loader()
      .then((data) => {
        if (mounted) {
          loadedForKeyRef.current = reloadKey;
          setState({ loading: false, error: "", data, loaded: true });
        }
      })
      .catch(() => {
        if (mounted) {
          loadedForKeyRef.current = reloadKey;
          setState({ loading: false, error: "Failed to load tab data.", data: null, loaded: true });
        }
      });

    return () => {
      mounted = false;
    };
  }, [active, loader, reloadKey]);

  return state;
}

function TabLoadState({ state, children }) {
  if (state.loading) return <BrandSpinner compact label="Loading tab..." py={8} />;
  if (state.error) return <Alert severity="error">{state.error}</Alert>;
  return children;
}

async function listEndpoint(endpoint, params) {
  const response = await api.get(endpoint, { params: { limit: 100, offset: 0, ...params } });
  return Array.isArray(response.data?.data) ? response.data.data : [];
}

async function getContactsForLinks(links) {
  const results = await Promise.allSettled(
    links.map((link) => api.get(`/membership/contacts/${link.contact_id}`)),
  );
  return results
    .map((result, index) =>
      result.status === "fulfilled"
        ? { ...result.value.data, link: links[index] }
        : { id: links[index].contact_id, full_name: `Contact #${links[index].contact_id}`, link: links[index] },
    );
}

function OverviewTab({ memberId, member }) {
  const loader = useCallback(async () => {
    const [memberships, memberContacts] =
      await Promise.all([
        listEndpoint("/membership/memberships", { member_id: memberId, sort_by: "renewal_date", sort_dir: "asc", limit: 5 }),
        listEndpoint("/membership/member-contacts", { member_id: memberId, sort_by: "created_at", sort_dir: "desc", limit: 5 }),
      ]);
    return { memberships, memberContacts };
  }, [memberId]);

  const state = useTabData(true, loader);

  return (
    <TabLoadState state={state}>
      <OverviewContent memberId={memberId} member={member} data={state.data || {}} />
    </TabLoadState>
  );
}

function EngagementScorePanel({ memberId, member }) {
  const [refreshKey, setRefreshKey] = useState(0);
  const [recalculating, setRecalculating] = useState(false);
  const loader = useCallback(
    () => api.get(`/membership/members/${memberId}/engagement-score/explanation`).then((response) => response.data),
    [memberId],
  );
  const state = useTabData(true, loader, refreshKey);
  const engagementScore = state.data || {};
  const scoreValue = engagementScore.score ?? member.engagement_score ?? 0;
  const scoreCategory = engagementScore.category || member.engagement_category || "unscored";
  const scoreBreakdown = Array.isArray(engagementScore.breakdown) ? engagementScore.breakdown : [];
  const hiddenScoreInputs = new Set([
    "referrals_made",
    "referrals_received",
    "introductions_made",
    "communication_engagement",
  ]);
  const visibleScoreBreakdown = scoreBreakdown.filter((item) => !hiddenScoreInputs.has(item.key));

  const recalculate = useCallback(async () => {
    setRecalculating(true);
    try {
      await api.post(`/membership/members/${memberId}/engagement-score/recalculate`);
      setRefreshKey((value) => value + 1);
    } finally {
      setRecalculating(false);
    }
  }, [memberId]);

  return (
    <Card sx={CARD_SX}>
      <CardContent sx={{ p: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 2 }}>
          <SectionHeader>Engagement Score Explanation</SectionHeader>
          <Button
            startIcon={<RefreshIcon />}
            onClick={recalculate}
            disabled={recalculating}
            sx={SECONDARY_BUTTON_SX}
          >
            {recalculating ? "Recalculating..." : "Recalculate Score"}
          </Button>
        </Box>
        {state.loading ? (
          <BrandSpinner compact label="Loading score..." py={4} />
        ) : state.error ? (
          <Alert severity="error">{state.error}</Alert>
        ) : (
          <>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <Typography sx={{ color: "#64748b", fontSize: 12, fontWeight: 800 }}>Score</Typography>
                <Typography sx={{ color: BRAND_BLUE, fontSize: 30, fontWeight: 800 }}>{scoreValue}</Typography>
                <EngagementCategoryChip category={scoreCategory} />
              </Grid>
              <Grid item xs={12} md={9}>
                <Typography sx={{ color: "#64748b", fontSize: 12, fontWeight: 800 }}>Progress</Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.max(0, Math.min(100, Number(scoreValue) || 0))}
                  sx={{
                    height: 10,
                    borderRadius: 2,
                    mt: 1,
                    bgcolor: "rgba(0,51,108,0.08)",
                    "& .MuiLinearProgress-bar": { bgcolor: BRAND_GREEN },
                  }}
                />
                <Typography sx={{ color: "#64748b", fontSize: 13, mt: 1 }}>
                  Last calculated {formatDateTime(engagementScore.calculated_at || member.engagement_score_calculated_at)}
                </Typography>
              </Grid>
            </Grid>
            <TableContainer sx={{ overflowX: "auto", mt: 2 }}>
              <Table size="small" sx={{ width: "100%" }}>
                <TableHead>
                  <TableRow>
                    {["Input", "Value", "Weight", "Points", "Why"].map((label) => (
                      <TableCell key={label} sx={{ bgcolor: BRAND_BLUE, color: "#fff", fontSize: 12, fontWeight: 800, textTransform: "uppercase" }}>
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleScoreBreakdown.map((item) => (
                    <TableRow key={item.key}>
                      <TableCell sx={{ color: BRAND_BLUE, fontWeight: 800 }}>{item.label}</TableCell>
                      <TableCell sx={{ color: BRAND_BLUE }}>{display(item.value)}</TableCell>
                      <TableCell sx={{ color: BRAND_BLUE }}>{display(item.weight)}</TableCell>
                      <TableCell sx={{ color: BRAND_BLUE, fontWeight: 800 }}>{display(item.points)}</TableCell>
                      <TableCell sx={{ color: "#64748b" }}>{display(item.explanation)}</TableCell>
                    </TableRow>
                  ))}
                  {!visibleScoreBreakdown.length ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ color: "#64748b" }}>No score breakdown is available.</TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function OverviewContent({ memberId, member, data }) {
  const currentMembership = getCurrentMembership(data.memberships || []);
  const primaryContactLink =
    (data.memberContacts || []).find((contact) => contact.is_primary) ||
    (data.memberContacts || [])[0];
  const scoreValue = member.engagement_score ?? 0;
  const scoreCategory = member.engagement_category || "unscored";
  const atRisk = getAtRiskIndicators(member, currentMembership);

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
          gap: 2,
        }}
      >
        <MetricCard label="Membership Tier" value={display(currentMembership?.membership_level)} />
        <MetricCard label="Renewal Date" value={formatDate(currentMembership?.renewal_date)} />
        <MetricCard label="Status" value={<StatusChip status={member.status} />} />
        <MetricCard
          label="Engagement Score"
          value={scoreValue}
          helper={
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <EngagementCategoryChip category={scoreCategory} />
            </Stack>
          }
        />
      </Box>

      <Stack spacing={2.5}>
        <EngagementScorePanel memberId={memberId} member={member} />

        <Card sx={CARD_SX}>
          <CardContent sx={{ p: 4 }}>
            <SectionHeader>Member Snapshot</SectionHeader>
            <Grid container spacing={2.5}>
              {[
                ["Organization", member.name],
                ["Member Type", member.member_type],
                ["Sector", member.sector],
                ["Phone", member.phone_number],
                ["Email", member.email],
                ["Website", member.website],
                ["Joined", formatDate(member.joined_at)],
              ].map(([label, value]) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={label}>
                  <Typography sx={{ color: "#64748b", fontSize: 12.5, fontWeight: 700 }}>{label}</Typography>
                  <Typography sx={{ color: BRAND_BLUE, fontWeight: 700, fontSize: 16, mt: 0.25 }}>{display(value)}</Typography>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        <Card sx={CARD_SX}>
          <CardContent sx={{ p: 4 }}>
            <SectionHeader>Relationship</SectionHeader>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography sx={{ color: "#64748b", fontSize: 12.5, fontWeight: 700 }}>Primary Contact</Typography>
                <Typography sx={{ color: BRAND_BLUE, fontWeight: 700, fontSize: 16, mt: 0.25 }}>
                  {primaryContactLink?.contact_id ? `Contact #${primaryContactLink.contact_id}` : "-"}
                </Typography>
                <Typography sx={{ color: "#64748b", fontSize: 13 }}>
                  {primaryContactLink?.role || member.email || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography sx={{ color: "#64748b", fontSize: 12.5, fontWeight: 700 }}>Relationship Owner</Typography>
                <Typography sx={{ color: BRAND_BLUE, fontWeight: 700, fontSize: 16, mt: 0.25 }}>-</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography sx={{ color: "#64748b", fontSize: 12.5, fontWeight: 700 }}>Notes Summary</Typography>
                <Typography sx={{ color: BRAND_BLUE, lineHeight: 1.6, mt: 0.25 }}>
                  {display(currentMembership?.notes || member.description)}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={CARD_SX}>
          <CardContent sx={{ p: 4 }}>
            <SectionHeader>At-risk Indicators</SectionHeader>
            {atRisk.length ? (
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 1.5 }}>
                {atRisk.map((item) => (
                  <Box
                    key={item}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1.5,
                      borderRadius: 2,
                      border: "1px solid #fde7c7",
                      bgcolor: "#fffaf0",
                    }}
                  >
                    <WarningAmberIcon sx={{ color: "#B7791F", fontSize: 20, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND_BLUE, fontWeight: 600 }}>{item}</Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography sx={{ color: BRAND_GREEN, fontWeight: 700 }}>
                No current at-risk indicators.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Stack>
    </Stack>
  );
}

function ContactsTab({ active, memberId }) {
  const [refreshKey, setRefreshKey] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeContact, setActiveContact] = useState(null);
  const [saving, setSaving] = useState(false);

  const loader = useCallback(async () => {
    const links = await listEndpoint("/membership/member-contacts", { member_id: memberId });
    return getContactsForLinks(links);
  }, [memberId]);
  const state = useTabData(active, loader, refreshKey);

  const saveContact = useCallback(
    async ({ contactId, linkId, contactPayload, linkPayload, shouldUpdateContact }) => {
      setSaving(true);
      try {
        let nextContactId = contactId;
        if (nextContactId && shouldUpdateContact) {
          await api.patch(`/membership/contacts/${nextContactId}`, contactPayload);
        } else if (!nextContactId) {
          const response = await api.post("/membership/contacts", contactPayload);
          nextContactId = response.data.id;
        }

        if (linkId) {
          await api.patch(`/membership/member-contacts/${linkId}`, linkPayload);
        } else {
          await api.post("/membership/member-contacts", {
            member_id: memberId,
            contact_id: Number(nextContactId),
            ...linkPayload,
          });
        }

        setDialogOpen(false);
        setActiveContact(null);
        setRefreshKey((value) => value + 1);
      } finally {
        setSaving(false);
      }
    },
    [memberId],
  );

  return (
    <TabLoadState state={state}>
      <Stack spacing={2}>
        <DataTable
          rows={state.data || []}
          emptyLabel="No contacts are linked to this member."
          footerContent={
            <Tooltip title="Add contact" {...MEMBERSHIP_TOOLTIP_PROPS}>
              <IconButton
                aria-label="Add contact"
                size="small"
                onClick={() => {
                  setActiveContact(null);
                  setDialogOpen(true);
                }}
                sx={{
                  width: 34,
                  height: 34,
                  border: `1px solid rgba(0,51,108,0.18)`,
                  color: BRAND_BLUE,
                  bgcolor: "rgba(0,51,108,0.04)",
                  "&:hover": {
                    bgcolor: "rgba(100,131,21,0.12)",
                    borderColor: "rgba(100,131,21,0.35)",
                    color: BRAND_GREEN,
                  },
                }}
              >
                <PersonAddIcon sx={{ fontSize: 19 }} />
              </IconButton>
            </Tooltip>
          }
          columns={[
            {
              key: "full_name",
              label: "Name",
              render: (row) => (
                <Button
                  variant="text"
                  onClick={() => {
                    setActiveContact(row);
                    setDialogOpen(true);
                  }}
                  sx={{
                    p: 0,
                    minWidth: 0,
                    color: BRAND_BLUE,
                    fontWeight: 800,
                    textTransform: "none",
                    textAlign: "left",
                    justifyContent: "flex-start",
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: BRAND_GREEN,
                      textDecoration: "underline",
                    },
                  }}
                >
                  {display(row.full_name)}
                </Button>
              ),
            },
            { key: "job_title", label: "Job Title" },
            { key: "email", label: "Email" },
            { key: "direct_phone_number", label: "Direct Phone" },
            { key: "cell_phone_number", label: "Mobile Phone" },
            { key: "role", label: "Role", render: (row) => display(row.link?.role) },
            { key: "is_primary", label: "Primary", render: (row) => (row.link?.is_primary ? "Yes" : "No") },
            { key: "is_billing", label: "Billing", render: (row) => (row.link?.is_billing ? "Yes" : "No") },
          ]}
        />
      </Stack>
      <ContactDialog
        open={dialogOpen}
        contact={activeContact}
        saving={saving}
        onClose={() => setDialogOpen(false)}
        onSubmit={saveContact}
      />
    </TabLoadState>
  );
}

function EventsTab() {
  return (
    <Card sx={CARD_SX}>
      <CardContent sx={{ p: 4, textAlign: "center" }}>
        <Typography sx={{ color: BRAND_BLUE, fontWeight: 800 }}>Event details are available</Typography>
        <Typography sx={{ color: "#64748b", mt: 0.75 }}>
          Use the Membership Events workspace to view and manage event records.
        </Typography>
      </CardContent>
    </Card>
  );
}

function ApiTableTab({ active, endpoint, params, columns, emptyLabel }) {
  const loader = useCallback(() => listEndpoint(endpoint, params), [endpoint, params]);
  const state = useTabData(active, loader);
  return (
    <TabLoadState state={state}>
      <DataTable rows={state.data || []} columns={columns} emptyLabel={emptyLabel} />
    </TabLoadState>
  );
}

function TimelineTab({ active, memberId }) {
  const loader = useCallback(async () => {
    const sponsorships = await listEndpoint("/membership/sponsorships", { member_id: memberId });

    return [
      ...sponsorships.map((item) => ({ type: "Sponsorship", label: item.name, date: item.start_date || item.created_at })),
    ].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [memberId]);

  const state = useTabData(active, loader);
  return (
    <TabLoadState state={state}>
      <Card sx={CARD_SX}>
        <CardContent sx={{ p: 3 }}>
          {(state.data || []).length ? (
            <Stack spacing={1.5}>
              {state.data.map((item, index) => (
                <Box key={`${item.type}-${index}`} sx={{ pl: 2, borderLeft: `3px solid ${BRAND_GREEN}` }}>
                  <Typography sx={{ color: BRAND_BLUE, fontWeight: 800 }}>
                    {item.type}: {display(item.label)}
                  </Typography>
                  <Typography sx={{ color: "#64748b", fontSize: 13 }}>{formatDateTime(item.date)}</Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography sx={{ color: "#64748b" }}>No timeline activity is recorded.</Typography>
          )}
        </CardContent>
      </Card>
    </TabLoadState>
  );
}

function ReportsTab() {
  return (
    <Card sx={CARD_SX}>
      <CardContent sx={{ p: 4, textAlign: "center" }}>
        <Typography sx={{ color: BRAND_BLUE, fontWeight: 800 }}>ROI reports are available</Typography>
        <Typography sx={{ color: "#64748b", mt: 0.75 }}>
          Use the Membership Reports workspace to preview, generate, export, and save member ROI reports.
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function MembershipMemberProfile() {
  const { id } = useParams();
  const memberId = Number(id);
  const [tabIndex, setTabIndex] = useState(0);
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadMember = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get(`/membership/members/${memberId}`);
      setMember(response.data);
    } catch {
      setError("Failed to load member profile.");
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    loadMember();
  }, [loadMember]);

  const saveMember = useCallback(
    async (payload) => {
      setSaving(true);
      try {
        const response = await api.patch(`/membership/members/${memberId}`, payload);
        setMember(response.data);
        setEditOpen(false);
      } finally {
        setSaving(false);
      }
    },
    [memberId],
  );

  const tabContent = useMemo(() => {
    if (!member) return null;
    const active = (label) => TABS[tabIndex] === label;

    switch (TABS[tabIndex]) {
      case "Overview":
        return <OverviewTab memberId={memberId} member={member} />;
      case "Contacts":
        return <ContactsTab active={active("Contacts")} memberId={memberId} />;
      case "Membership History":
        return (
          <ApiTableTab
            active={active("Membership History")}
            endpoint="/membership/memberships"
            params={{ member_id: memberId, sort_by: "renewal_date", sort_dir: "desc" }}
            emptyLabel="No membership records are recorded."
            columns={[
              { key: "membership_level", label: "Tier" },
              { key: "status", label: "Status" },
              { key: "start_date", label: "Start", render: (row) => formatDate(row.start_date) },
              { key: "renewal_date", label: "Renewal", render: (row) => formatDate(row.renewal_date) },
              { key: "dues_amount", label: "Dues" },
              { key: "billing_frequency", label: "Billing" },
            ]}
          />
        );
      case "Events":
        return <EventsTab />;
      case "Sponsorships":
        return (
          <ApiTableTab
            active={active("Sponsorships")}
            endpoint="/membership/sponsorships"
            params={{ member_id: memberId }}
            emptyLabel="No sponsorships are recorded."
            columns={[
              { key: "name", label: "Name" },
              { key: "sponsorship_level", label: "Level" },
              { key: "status", label: "Status" },
              { key: "amount", label: "Amount" },
              { key: "start_date", label: "Start", render: (row) => formatDate(row.start_date) },
              { key: "end_date", label: "End", render: (row) => formatDate(row.end_date) },
            ]}
          />
        );
      case "Files":
        return (
          <ApiTableTab
            active={active("Files")}
            endpoint="/membership/files"
            params={{ entity_type: "member", entity_id: memberId }}
            emptyLabel="No files are attached."
            columns={[
              { key: "original_name", label: "File" },
              { key: "content_type", label: "Type" },
              { key: "file_size_bytes", label: "Size" },
              { key: "description", label: "Description" },
              { key: "created_at", label: "Uploaded", render: (row) => formatDateTime(row.created_at) },
            ]}
          />
        );
      case "Reports":
        return <ReportsTab />;
      case "Timeline":
        return <TimelineTab active={active("Timeline")} memberId={memberId} />;
      default:
        return null;
    }
  }, [member, memberId, tabIndex]);

  if (loading) {
    return <BrandSpinner label="Loading member profile..." />;
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={<Button onClick={loadMember}>Retry</Button>}>{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", backgroundColor: PAGE_BG, pb: 4 }}>
      <Box sx={{ px: { xs: 2, md: 3 }, pt: 3 }}>
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            borderRadius: 3,
            overflow: "hidden",
            border: "1px solid rgba(0,51,108,0.10)",
            boxShadow: "0 2px 12px rgba(0,51,108,0.08)",
            bgcolor: "white",
          }}
        >
          <Box sx={{ p: 3, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, minWidth: 0 }}>
              <BackButton to="/membership/members" />
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2,
                  background: BRAND_GREEN,
                  color: "#ffffff",
                  display: "grid",
                  placeItems: "center",
                  flex: "0 0 auto",
                }}
              >
                <BusinessIcon sx={{ fontSize: 24 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: BRAND_BLUE, fontSize: 26, fontWeight: 800, lineHeight: 1.1 }}>
                  {member.name}
                </Typography>
                <Typography sx={{ color: "#64748b", fontSize: 14, mt: 0.75 }}>
                  {[member.member_type, member.city, member.state].filter(Boolean).join(" · ") || "Member profile"}
                </Typography>
              </Box>
            </Box>
            <Button startIcon={<EditIcon />} onClick={() => setEditOpen(true)} sx={PRIMARY_BUTTON_SX}>
              Edit Member
            </Button>
          </Box>

          <Box sx={{ px: 3, borderTop: "1px solid rgba(0,51,108,0.08)" }}>
            <Tabs
              value={tabIndex}
              onChange={(_event, value) => setTabIndex(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 14,
                  minHeight: 44,
                  color: "text.secondary",
                  "&.Mui-selected": { color: BRAND_BLUE },
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: BRAND_GREEN,
                  height: 3,
                  borderRadius: "3px 3px 0 0",
                },
              }}
            >
              {TABS.map((label) => (
                <Tab key={label} label={label} />
              ))}
            </Tabs>
          </Box>
        </Paper>

        <Box>{tabContent}</Box>
      </Box>

      <MembershipMemberDialog
        open={editOpen}
        member={member}
        saving={saving}
        onClose={() => setEditOpen(false)}
        onSubmit={saveMember}
      />
    </Box>
  );
}
